/**
 * Memoria-specific event helpers.
 * 
 * Thin wrapper around the generic dispatchEvent function for Memoria game events.
 * Memoria has three event types:
 * - 'guess': Submit two selected words to check if they form a valid pair
 * - 'hint_reveal_all': Show all cards temporarily for 10 seconds
 * - 'hint_reveal_pair': Reveal one unmatched pair automatically
 * 
 * Usage:
 * ```javascript
 * const { dispatchEvent, dispatchHintEvent } = useGameplaySession('memoria', date);
 * const { submitGuess, revealAll, revealPair } = useMemoriaEvents(dispatchEvent, dispatchHintEvent);
 * 
 * const result = await submitGuess('naturale', 'naturalmente');
 * if (result.success) {
 *   // result.result contains: { valid, color?, lives_remaining, pairs_found, game_complete, solutions? }
 * }
 * ```
 */

import { useCallback } from 'react';

/**
 * Hook providing Memoria-specific event helpers.
 * 
 * @param {function} dispatchEvent - The dispatchEvent function from useGameplaySession
 * @param {function} dispatchHintEvent - The dispatchHintEvent function from useGameplaySession
 * @returns {object} Memoria event helpers
 * @returns {function} returns.submitGuess - Submit two selected words to check for a valid pair
 * @returns {function} returns.revealAll - Request to show all cards for 10 seconds (hint)
 * @returns {function} returns.revealPair - Request to reveal one unmatched pair (hint)
 */
export function useMemoriaEvents(dispatchEvent, dispatchHintEvent) {
  /**
   * Submit two selected words to check if they form a valid pair.
   * 
   * @param {string} word1 - First selected word
   * @param {string} word2 - Second selected word
   * @returns {Promise<{
   *   success: boolean,
   *   result?: {
   *     valid: boolean,
   *     color?: string,
   *     lives_remaining: number,
   *     pairs_found: number,
   *     game_complete: boolean,
   *     solutions?: Array<{color: string, words: [string, string]}>
   *   },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await submitGuess('naturale', 'naturalmente');
   * if (result.success) {
   *   if (result.result.valid) {
   *     // Pair found! Use result.result.color
   *   } else {
   *     // Wrong guess - check result.state.lives for remaining lives
   *   }
   * }
   */
  const submitGuess = useCallback(async (word1, word2) => {
    return dispatchEvent('guess', { words: [word1, word2] });
  }, [dispatchEvent]);

  /**
   * Request to reveal all cards temporarily for 10 seconds (hint).
   * Requires authentication and sufficient gettoncini balance.
   * Player cannot interact with cards during the reveal.
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { duration_seconds: number },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await revealAll(() => showSignupModal());
   * if (result.success) {
   *   // Start 10-second countdown, show all cards
   * }
   */
  const revealAll = useCallback(async (onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_reveal_all', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  /**
   * Request to reveal one unmatched pair automatically (hint).
   * Requires authentication and sufficient gettoncini balance.
   * The revealed pair is automatically matched (doesn't cost a life).
   * 
   * @param {function} onNotAuthenticated - Callback when user is not signed in
   * @returns {Promise<{
   *   success: boolean,
   *   hint_data?: { pair: [string, string], color: string, pairs_found: number, game_complete: boolean },
   *   state?: object,
   *   error?: string,
   *   message?: string
   * }>}
   * 
   * @example
   * const result = await revealPair(() => showSignupModal());
   * if (result.success) {
   *   const { pair, color } = result.hint_data;
   *   // Pair is already matched in state, UI should update from state
   * }
   */
  const revealPair = useCallback(async (onNotAuthenticated = null) => {
    return dispatchHintEvent('hint_reveal_pair', {}, onNotAuthenticated);
  }, [dispatchHintEvent]);

  return {
    submitGuess,
    revealAll,
    revealPair,
  };
}

